import { ErrorNotification, Input, Password, PrimaryButton, Tooltip } from '@cb/apricot-react';
import { Grid, LinearProgress } from '@mui/material';
import { ChangeEventHandler, FC, FormEvent, useState } from 'react';
import { Auth } from 'aws-amplify';

import { IRegisterForm } from 'src/types';
import {
  formatDate,
  validateEmailPattern,
  validatePassword,
  validatePasswordMatch,
  validateUserName,
} from 'src/utils';

import { Typography } from 'src/components';
import { useAuth } from 'src/contexts';

interface RegisterFormProps {
  onComplete: (form: IRegisterForm) => void;
}

const RegisterForm: FC<RegisterFormProps> = ({ onComplete }) => {
  const { isAI } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const [showFormStatus, setShowFormStatus] = useState(false);

  const registerTooltip = "Level I - Authorization: Allows schools to receive their students’ Pre-AP, AP Exam, PSAT, " +
    "SAT and other College Board test scores. Schools cannot administer AP, PSAT, SAT or other College Board exams, " +
    "but can participate in the AP Course Audit, and in the Pre-AP program.\n " +
    "Level II - Authorization: Allows for schools to receive scores as well as administer AP Exams " +
    "and PSAT-related assessments and to apply to become a SAT Test Center.";
  const tooltipId = "toolTipTextId";

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirm] = useState('');
  const [email, setEmail] = useState('');

  const isValidName = validateUserName(name);
  const isValidPassword = validatePassword(password);
  const isValidConfirmPassword = validatePasswordMatch(password, confirmPassword);
  const isValidEmail = validateEmailPattern(email);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    const isValidForm = isValidName && isValidPassword && isValidConfirmPassword && isValidEmail;
    if (!isValidForm) {
      setShowFormStatus(true);
      return;
    }

    setIsLoading(true);
    try {
      await Auth.signUp(name, password, email);
      onComplete({
        name,
        password,
        confirmPassword,
        email
      });
      setErrorMessage(null);
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {isLoading && <LinearProgress color="secondary" />}
      {errorMessage && (
        // @ts-ignore
        <ErrorNotification dismissible title="Error">
          {errorMessage}
        </ErrorNotification>
      )}

      <Grid container spacing={3} sx={{ p: 3, px: 5 }}>
        <Grid item xs={12} >
          <div id={tooltipId}>
            <Typography variant="body2">
              A College Board Code Request account is needed to request a College Board Code for the first time, to update permissions or programs offered for an existing College Board Code, or to update organization data (including institution name and address) for an existing College Board code.
              Your College Board Code Request account allows you to view the status of a request in real time and communicate directly with reviewers, making the request process more efficient than ever before!<b> All you need is a desired username and email address in the fields below.</b> Once your email is validated, you are all set. Just log in and submit your College Board Code Request Form to apply to gain access to more College Board products and tools!
              <b> Please Note:</b> This account is separate and unique from your College Board Professional account. College Board Code Request accounts are used to track requests regarding College Board Codes.
            </Typography>
          </div>
          
          {/** @ts-ignore */}
          { isAI && 
            <div>
              <Tooltip placement="bottom" trigger={tooltipId}>
                <p>{registerTooltip}</p>
              </Tooltip>
            </div> }
        </Grid>
        <Grid item xs={12}>
          <Input
            name="name"
            label="Username"
            placeholder="Name"
            required
            value={name}
            validation={showFormStatus && !isValidName ? 'error' : undefined}
            validationMsg={!isValidName ? 'Invalid username, must contain at least 3 characters' : ''}
            onChange={(e): void => { setName(e.target.value); }}
          />
        </Grid>

        <Grid item xs={12}>
          <Password
            name="password"
            label="Password"
            placeholder="Password"
            required
            value={password}
            validation={showFormStatus && !isValidPassword ? 'error' : undefined}
            validationMsg={!isValidPassword ? 'Minimum length: 8, uppercase letter, lowercase letter, and number' : ''}
            onChange={(e): void => { setPassword(e.target.value); }}
          />
          <div>
            <Typography variant="body2">
              Requirements: Minimum length: 8, uppercase letter, lowercase letter, and number
            </Typography>
          </div>
        </Grid>

        <Grid item xs={12}>
          <Password
            name="confirmPassword"
            label="Confirm Password"
            placeholder="Confirm Password"
            required
            value={confirmPassword}
            validation={showFormStatus && !isValidConfirmPassword ? 'error' : undefined}
            validationMsg={!isValidConfirmPassword ? 'Password does not match' : ''}
            onChange={(e): void => { setConfirm(e.target.value); }}
          />
        </Grid>

        <Grid item xs={12}>
          <Input

            name="email"
            label="Email"
            placeholder="Email"
            required
            value={email}
            validation={showFormStatus && !isValidEmail ? 'error' : undefined}
            validationMsg={!isValidEmail ? 'Invalid email format' : ''}
            onChange={(e): void => { setEmail(e.target.value); }}
          />
        </Grid>


        <Grid item xs={12}>
          <Grid container alignItems="center">
            <PrimaryButton type="submit" style={{ width: '100%' }}>
              Register
            </PrimaryButton>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

export default RegisterForm;
